import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [teacher, setTeacher] = useState(null);
  const [student, setStudent] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState(null); // 'teacher' or 'student'

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUserType = localStorage.getItem('userType');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUserType && storedUser) {
      setToken(storedToken);
      setUserType(storedUserType);
      
      const user = JSON.parse(storedUser);
      if (storedUserType === 'teacher') {
        setTeacher(user);
      } else if (storedUserType === 'student') {
        setStudent(user);
      }
    }
    setLoading(false);
  }, []);

  // Single-device enforcement (frontend half): if the backend rejects an
  // authenticated STUDENT request with 401 (because a newer login rotated the
  // session id on the server), clear this device's session and send it to login.
  // Scoped to student API calls and never the login call itself.
  useEffect(() => {
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await origFetch(...args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        if (
          res.status === 401 &&
          localStorage.getItem('userType') === 'student' &&
          localStorage.getItem('token') &&
          url.includes('/api/') &&
          !url.includes('/auth/')
        ) {
          localStorage.removeItem('token');
          localStorage.removeItem('userType');
          localStorage.removeItem('user');
          if (!window.location.pathname.includes('/student/login')) {
            window.location.href = '/student/login';
          }
        }
      } catch { /* ignore */ }
      return res;
    };
    return () => { window.fetch = origFetch; };
  }, []);

  // Teacher Registration
  const registerTeacher = useCallback(async (name, email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/teacher/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.detail || 'فشل التسجيل');
      }

      setToken(data.token);
      setTeacher(data.teacher);
      setUserType('teacher');
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('userType', 'teacher');
      localStorage.setItem('user', JSON.stringify(data.teacher));

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [API_URL]);

  // Teacher Login
  const loginTeacher = useCallback(async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/teacher/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error('فشل تسجيل الدخول - خطأ في الخادم');
      }

      if (!response.ok) {
        throw new Error(data?.detail || 'فشل تسجيل الدخول');
      }

      setToken(data.token);
      setTeacher(data.teacher);
      setUserType('teacher');
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('userType', 'teacher');
      localStorage.setItem('user', JSON.stringify(data.teacher));

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [API_URL]);

  // Student Registration (NEW)
  const registerStudent = useCallback(async (formData) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/student/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.first_name,
          second_name: formData.second_name,
          third_name: formData.third_name,
          family_name: formData.family_name,
          username: formData.username,
          password: formData.password,
          date_of_birth: formData.date_of_birth,
          phone_number: formData.phone_number,
          school: formData.school,
          grade: formData.grade,
          section: formData.section,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.detail || 'فشل التسجيل');
      }

      setToken(data.token);
      setStudent(data.student);
      setUserType('student');
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('userType', 'student');
      localStorage.setItem('user', JSON.stringify(data.student));

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [API_URL]);

  // Get or create device ID
  const getDeviceId = useCallback(() => {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  }, []);

  // Student Login (UPDATED)
  const loginStudent = useCallback(async (username, password) => {
    try {
      const deviceId = getDeviceId();
      const response = await fetch(`${API_URL}/api/auth/student/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, device_id: deviceId }),
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error('فشل تسجيل الدخول - خطأ في الخادم');
      }

      if (!response.ok) {
        throw new Error(data?.detail || 'فشل تسجيل الدخول');
      }

      setToken(data.token);
      setStudent(data.student);
      setUserType('student');
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('userType', 'student');
      localStorage.setItem('user', JSON.stringify(data.student));

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [API_URL, getDeviceId]);

  // Local-only logout (used when backend already says "not authorized").
  // Skips the backend logout call (which would 401 again).
  const hardLogout = useCallback(() => {
    setToken(null);
    setTeacher(null);
    setStudent(null);
    setUserType(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('user');
  }, []);

  // Refresh student status. If the backend says the account is gone (401)
  // or no longer exists (404 on /me), we clear local auth so deleted /
  // deactivated students don't keep navigating through cached UI.
  const refreshStudentStatus = useCallback(async () => {
    if (!token || userType !== 'student') return null;

    try {
      const response = await fetch(`${API_URL}/api/auth/student/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStudent(data);
        localStorage.setItem('user', JSON.stringify(data));
        return data;
      }
      if (response.status === 401 || response.status === 404) {
        hardLogout();
      }
    } catch (error) {
      console.error('Error refreshing student status:', error);
    }
    return null;
  }, [API_URL, token, userType, hardLogout]);

  // Auto-sync student profile every 30 seconds (was 90s) — picks up teacher
  // edits AND catches account deletion/deactivation faster.
  useEffect(() => {
    if (userType !== 'student' || !token) return;
    refreshStudentStatus(); // immediate check on mount
    const interval = setInterval(refreshStudentStatus, 30000);
    const onFocus = () => refreshStudentStatus();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [userType, token, refreshStudentStatus]);

  // Global 401 interceptor. Any /api/ call returning 401 means the token is
  // no longer valid (expired, revoked, or wrong JWT_SECRET on server restart).
  // Clear local auth so the next render shows the login screen.
  useEffect(() => {
    if (!token) return;
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        const req = args[0];
        const url = typeof req === 'string' ? req : (req && req.url) || '';
        // Skip auth-flow endpoints — 401 there means "wrong password", not "session dead"
        const isAuthFlow = url.includes('/api/auth/');
        if (response.status === 401 && url.includes('/api/') && !isAuthFlow) {
          hardLogout();
        }
      } catch { /* swallow */ }
      return response;
    };
    return () => { window.fetch = originalFetch; };
  }, [token, hardLogout]);

  // Logout
  const logout = useCallback(async () => {
    // If student, call backend to clear session
    if (userType === 'student' && token) {
      try {
        await fetch(`${API_URL}/api/auth/student/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('Error logging out:', error);
      }
    }
    
    setToken(null);
    setTeacher(null);
    setStudent(null);
    setUserType(null);
    
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('user');
  }, [API_URL, token, userType]);

  // Check if authenticated
  const isAuthenticated = Boolean(token);
  const isTeacher = userType === 'teacher';
  const isStudent = userType === 'student';
  const isApprovedStudent = isStudent && student?.status === 'approved';
  const isPendingStudent = isStudent && student?.status === 'pending';
  const isRejectedStudent = isStudent && student?.status === 'rejected';

  const value = {
    teacher,
    student,
    token,
    loading,
    userType,
    isAuthenticated,
    isTeacher,
    isStudent,
    isApprovedStudent,
    isPendingStudent,
    isRejectedStudent,
    registerTeacher,
    loginTeacher,
    registerStudent,
    loginStudent,
    refreshStudentStatus,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
